import "app/news/components/latest-news/LatestCompanyNewsComponent.scss";
import template from "./LatestCompanyNewsComponent.html";
import {IComponentOptions, IOnDestroy} from "angular";
import {CodeAndExchange, securityMatcher} from "../../../instruments/types/CodeAndExchange";
import {Observable, ReplaySubject} from "rxjs";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {QuoteStore} from "../../../quote/store/QuoteStore";
import {FeatureNames} from "../../../types/UserConfig";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {NewsHeadlineIndicatorSourceFactory} from "../../service/source/NewsHeadlineIndicatorSourceFactory";
import {NewsHeadlineIndicatorModel} from "../../models/NewsHeadlineIndicatorModel";

export const LatestCompanyNews = "tfLatestCompanyNews";

export function latestCompanyNews(): IComponentOptions {
    return {
        template,
        bindings: {
            security: "<"
        },
        controllerAs: "ctrl",
        controller: LatestCompanyNewsComponentController
    };
}

export class LatestCompanyNewsComponentController implements IOnDestroy {

    /** @input */
    public set security(security: CodeAndExchange | null) {
        if (security == null) {
            this.security$.next(null);
        } else {
            const copy = ObjectUtil.pick(security, "code", "exchange");
            this.security$.next(copy);
        }
    }

    private readonly security$ = new ReplaySubject<CodeAndExchange | null>(1);

    private readonly latestNewsItem$: Observable<NewsHeadlineIndicatorModel | null>;

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly currentUserService: CurrentUserService,
                       private readonly quoteStore: QuoteStore,
                       private readonly newsHeadlineIndicatorSourceFactory: NewsHeadlineIndicatorSourceFactory) {
        this.latestNewsItem$ = this.buildLatestNewsItem();
    }

    private buildLatestNewsItem(): Observable<NewsHeadlineIndicatorModel | null> {
        const hasFeatures$ = this.buildHasFeatures();
        const security$ = this.security$.distinctUntilChanged((a, b) => this.securityUnchanged(a, b));
        const source$ = Observable.combineLatest(hasFeatures$, security$);
        return source$.switchMap(([hasFeature, security]) => this.latestNewsItem(hasFeature, security))
                      .startWith(null)
                      .publishReplay(1)
                      .refCount();
    }

    private securityUnchanged(a: CodeAndExchange | null, b: CodeAndExchange | null): boolean {
        if ((a == null) || (b == null)) {
            return a === b;
        } else {
            return securityMatcher(a, b);
        }
    }

    private latestNewsItem(hasFeature: boolean,
                           security: CodeAndExchange | null): Observable<NewsHeadlineIndicatorModel | null> {
        if (hasFeature && (security != null)) {
            return this.newsHeadlineIndicatorSourceFactory.indicator(security);
        } else {
            return Observable.of(null);
        }
    }

    private buildHasFeatures(): Observable<boolean> {
        return this.currentUserService
                   .current
                   .map((u) => u.hasFeature(FeatureNames.NEWS))
                   .startWith(false)
                   .publishReplay(1)
                   .refCount();
    }

    public $onDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
